/**
 * Firm Success — one firm's page (#676; the ratified fs-page-676.html
 * design over live API data).
 *
 * Sections are titled by Chris's own questions (the session-03 ruling: the
 * spec is the question list, sections answer them in plain vocabulary).
 * Above the templates (#1267): a headline slot — the Firm's routing status
 * until Firm Success's foundation document (Firm-Success#136) rules which fact
 * leads — then "Can they receive Referrals?": routing, the documents the
 * Firm's designation makes applicable, and its primary Contact.
 *
 * A Both-capacity firm carries the OF/HF toggle at the top (R4); the OF and
 * HF templates each render:
 *   OF: 90-day sends graph (R7/R10) · status cards (R8/R11) · Mon–Sun
 *       calendar · type pie · unmatched ·
 *       destinations table · origin map (R12).
 *   HF: 90-day routed graph + dashed reached-Placed line (R13) · status cards ·
 *       calendar · TWO time clocks (R15 as
 *       amended session 05 — send→decision and FSA return, medians, each
 *       beside the firm's own 30-day blocks, #1267) ·
 *       origin map with type layers (R17).
 *
 * A pie one case type fills, a map that shades one state and a calendar of
 * zeros are written as one sentence each instead of drawn (#1267).
 *
 * Every block reads the live book, and windows are PER CARD (#680): lifetime
 * for the censuses, maps and destinations; a rolling 90 days for
 * the two clocks; the chart's own window for the graphs and the type mix.
 *
 * Captions are thin by rule. The <Term> hover glossary was wiped with the
 * card/definition layer (#740); ruled vocabulary lives in the Contract
 * (engineering/contract/glossary.yaml).
 */

import { useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  applicableArtifactsFor,
  type ClientSuccessFirm,
  type FsHfPage,
  type FsOfPage,
  type IntakeArtifact,
} from "@command-center/shared";
import { ClientSuccessNav } from "@/components/client-success-nav";
import { useFirmSuccessFirm } from "@/lib/use-firm-success";
import { useClientSuccessFirms } from "@/lib/use-client-success-firms";
import { FirmCaseNotes } from "@/components/firm-case-notes";
import {
  CalendarBlock,
  ClockTable,
  DailyGraph,
  FsMapBlock,
  MixPie,
  QCard,
  StatusCardsRow,
  calendarAllZero,
  emptyCalendarLine,
  graphWindowNote,
  mapAsLine,
  mixAsLine,
} from "./fs-blocks";
import "./fs.css";

export function FirmSuccessFirmPage() {
  const { firmId } = useParams({ from: "/firm-success/firm-success/$firmId" });
  const { cap: capFromList } = useSearch({
    from: "/firm-success/firm-success/$firmId",
  });
  const navigate = useNavigate();
  const firm = useFirmSuccessFirm(firmId);
  const [cap, setCap] = useState<"of" | "hf" | null>(null);

  const data = firm.data;
  // Toggle click wins; then the capacity the list was clicked from; then the
  // firm's only capacity. Without the middle term, clicking a firm in the
  // handling list opened its originating page — which for a firm like VKV is
  // a near-empty view of a firm we route hundreds of cases to.
  const preferred = cap ?? capFromList ?? null;
  const activeCap =
    preferred !== null && data?.capacities.includes(preferred)
      ? preferred
      : (data?.capacities[0] ?? "of");

  return (
    <div className="p-6">
      <ClientSuccessNav active="firm-success" />
      <div className="fs-surface" style={{ margin: "-8px -24px 0", borderRadius: 12 }}>
        <div className="shell">
          <div className="crumb">
            <button
              type="button"
              className="back"
              onClick={() => void navigate({ to: "/firm-success/firm-success" })}
              data-testid="fs-back"
            >
              ← All firms
            </button>
            <div>
              <h1>{data?.name ?? "…"}</h1>
              <p className="sub" style={{ margin: "2px 0 0" }}>
                {activeCap === "of"
                  ? "As an originating firm — the leads they send us."
                  : "As a handling firm — the cases we route to them."}{" "}
                Each block states its own window.
              </p>
            </div>
            {data !== undefined && data.capacities.length > 1 ? (
              <span className="captoggle" data-testid="fs-firm-captoggle">
                <button type="button" aria-pressed={activeCap === "of"} onClick={() => setCap("of")}>
                  Sends us leads
                </button>
                <button type="button" aria-pressed={activeCap === "hf"} onClick={() => setCap("hf")}>
                  Handles cases
                </button>
              </span>
            ) : null}
          </div>
          <FirmFacts firmId={firmId} />
          {firm.isLoading ? <p className="soft">Loading…</p> : null}
          {firm.isError ? <p className="soft">Could not load this firm.</p> : null}
          {data !== undefined && activeCap === "of" && data.of !== null ? (
            <OfTemplate firmId={firmId} page={data.of} />
          ) : null}
          {data !== undefined && activeCap === "hf" && data.hf !== null ? (
            <HfTemplate firmId={firmId} page={data.hf} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function OfTemplate({ page, firmId }: { page: FsOfPage; firmId: string }) {
  const [openCard, setOpenCard] = useState<string | null>(null);
  const placedCardNow = page.statusCards.find((c) => c.key === "Placed")?.count ?? 0;
  return (
    <>
      <QCard
        title="Are they still sending?"
        accent="var(--q-send)"
        win={<>Leads they sent us, by the day each arrived. {graphWindowNote(page.daily)}</>}
      >
        <DailyGraph series={page.daily} totalLabel="leads" />
      </QCard>

      <QCard
        title="Where do their leads stand?"
        accent="var(--q-pipe)"
        win={
          <>
            All their leads, right now. Click a card for those cases. A lead nobody took is{" "}
            Rejected.
          </>
        }
      >
        <StatusSection
          page={page}
          openCard={openCard}
          setOpenCard={setOpenCard}
          dateHeading="Arrived"
          showExactStatus={false}
          shareOf={{ key: "Rejected", total: page.totalLeads }}
        />
      </QCard>

      <QCard
        title="Is this normal for them?"
        accent="var(--q-send)"
        win={<>Their last five weeks, Monday to Sunday. Weekly total on the right.</>}
      >
        {calendarAllZero(page.calendar) ? (
          <div className="answer" data-testid="fs-calendar-line">
            {emptyCalendarLine("No Leads arrived", page.daily)}
          </div>
        ) : (
          <CalendarBlock weeks={page.calendar} />
        )}
      </QCard>

      <div className="cols" style={{ marginBottom: 18 }}>
        <QCard
          title="What types of leads do they send?"
          accent="var(--q-type)"
          win={
            <>
              {page.mixTotal} leads, last 90 days — the same
              window as the graph above.
            </>
          }
        >
          {mixAsLine(page.mix, page.mixTotal) !== null ? (
            <div className="answer" data-testid="fs-mix-line">
              {mixAsLine(page.mix, page.mixTotal)}
            </div>
          ) : (
            <MixPie mix={page.mix} total={page.mixTotal} />
          )}
        </QCard>
      </div>

      <QCard title="How many of their leads could we not place?" accent="var(--q-pipe)" win={<>Right now.</>}>
        <div className="answer">
          For <b>{page.unmatchedNow}</b> of their {page.totalLeads} leads we had no handling firm to send to
          (Unmatched).
          {page.neverRouted > 0 ? (
            <>
              {" "}
              A further <b>{page.neverRouted}</b> of their {page.neverRoutedOf}{" "}
              classifiable leads were never routed.
            </>
          ) : null}
          {page.unclassifiable > 0 ? (
            <>
              {" "}
              {/* The segment beside the number (provenance migration_2026_04):
                  a lead the April 2026 migration moved carries only what its
                  pass brought — 04-06's first pass and the 04-20 batch came
                  without prior placement data, 04-06's second pass and the
                  evening waves with the placements v1 held — so a
                  missing placement on any of them says nothing about routing. */}
              <span className="fs-est">
                {page.unclassifiable} were moved in by the April 2026 migration —
                the first 04-06 pass and the 04-20 batch without prior placement
                data, the second 04-06 pass and the evening waves with the
                placements v1 held — and cannot be classified routed or never
                routed.
              </span>
            </>
          ) : null}
        </div>
      </QCard>

      <QCard
        title="Where do their leads go?"
        accent="var(--q-conv)"
        win={
          <>
            Every handling firm that received their {page.totalLeads} leads, and
            what happened. Received = placed + declined + still with them.
          </>
        }
      >
        <table>
          <thead>
            <tr>
              <th>Handling firm</th>
              <th className="r">Received</th>
              <th className="r">Placed</th>
              <th className="r">Declined</th>
              <th className="r">Still with them</th>
            </tr>
          </thead>
          <tbody>
            {page.destinations.map((d) => (
              <tr key={d.firmId}>
                <td>{d.name}</td>
                <td className="r num">{d.received}</td>
                <td className="r num">{d.placed}</td>
                <td className="r num">{d.declined}</td>
                <td className="r num">{d.awaiting}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </QCard>

      <QCard
        title="Where do their leads come from?"
        accent="var(--q-map)"
        win={
          <>
            By the state where the case arises.{" "}
            Darker = more. Click a state for its
            case types.
          </>
        }
      >
        {mapAsLine(page.map, page.mapNoState) !== null ? (
          <div className="answer" data-testid="fs-map-line">
            {mapAsLine(page.map, page.mapNoState)}
          </div>
        ) : (
          <FsMapBlock map={page.map} noState={page.mapNoState} total={page.totalLeads} sideTitle="Top states" chips={false} />
        )}
      </QCard>

      <QCard
        title="What have we noticed about working with them?"
        accent="var(--q-pipe)"
        win={
          <>
            What LF actually observed — calls, chases, reasons — as opposed to what the
            system recorded happening. Internal only; the firm never sees these.
          </>
        }
      >
        <FirmCaseNotes firmId={firmId} capacity="of" />
      </QCard>
    </>
  );
}

function HfTemplate({ page, firmId }: { page: FsHfPage; firmId: string }) {
  const [openCard, setOpenCard] = useState<string | null>(null);
  const placedCardNow = page.statusCards.find((c) => c.key === "Placed")?.count ?? 0;
  // Assigned (not sent) sits in no card, so it is added back for the map's
  // total to foot with the map, which counts every Placement (#1268).
  const totalPlacements =
    page.statusCards.reduce((s, c) => s + c.count, 0) + page.notSent;
  return (
    <>
      <QCard
        title="Are they responding to what we send?"
        accent="var(--q-resp)"
        win={
          <>
            Leads we sent them, by the day each was sent. The dashed line is cases that{" "}
            reached Placed that day; a case Dropped later stays on it.{" "}
            {graphWindowNote(page.daily)}
          </>
        }
      >
        <DailyGraph
          series={page.daily}
          converted={page.convertedByDay}
          convertedLabel="reached Placed"
          totalLabel="leads sent"
        />
      </QCard>

      <QCard
        title="Where do their cases stand?"
        accent="var(--q-pipe)"
        win={
          <>
            Everything we've sent them, right now. Click a card for those cases. A{" "}
            pull counts with Declined.
          </>
        }
      >
        <StatusSection page={page} openCard={openCard} setOpenCard={setOpenCard} dateHeading="Sent to them" showExactStatus={true} />
      </QCard>

      <QCard
        title="How much are we routing them?"
        accent="var(--q-send)"
        win={<>Their last five weeks, Monday to Sunday. Weekly total on the right.</>}
      >
        {calendarAllZero(page.calendar) ? (
          <div className="answer" data-testid="fs-calendar-line">
            {emptyCalendarLine("No Leads sent to them", page.daily)}
          </div>
        ) : (
          <CalendarBlock weeks={page.calendar} />
        )}
      </QCard>

      <QCard
        title="How fast do they move?"
        accent="var(--q-time)"
        win={
          <>
            Median days over the last 90 days, with the n each rests on, then
            the same days in 30-day blocks: this firm against its own months.
          </>
        }
      >
        <div className="time2">
          <ClockTable title="Decide" clockNote="From our send to their answer." rows={page.decideClock} />
          <ClockTable
            title="Return the signed FSA"
            clockNote="From their accept to the signed agreement coming back."
            rows={page.fsaClock}
          />
        </div>
      </QCard>

      <QCard
        title="Where do the leads we send them arise?"
        accent="var(--q-map)"
        win={
          <>
            By the state where the case arises.{" "}
            Darker = more. Chips re-shade one case
            type at a time; click a state for its case types.
          </>
        }
      >
        {mapAsLine(page.map, page.mapNoState) !== null ? (
          <div className="answer" data-testid="fs-map-line">
            {mapAsLine(page.map, page.mapNoState)}
          </div>
        ) : (
          <FsMapBlock map={page.map} noState={page.mapNoState} total={totalPlacements} sideTitle="Top states" chips={true} />
        )}
      </QCard>

      <QCard
        title="What have we noticed about working with them?"
        accent="var(--q-pipe)"
        win={
          <>
            What LF actually observed — calls, chases, reasons — as opposed to what the
            system recorded happening. Internal only; the firm never sees these.
          </>
        }
      >
        <FirmCaseNotes firmId={firmId} capacity="hf" />
      </QCard>
    </>
  );
}

function StatusSection({
  page,
  openCard,
  setOpenCard,
  dateHeading,
  showExactStatus,
  shareOf,
}: {
  page: FsOfPage | FsHfPage;
  openCard: string | null;
  setOpenCard: (k: string | null) => void;
  dateHeading: string;
  showExactStatus: boolean;
  shareOf?: { key: string; total: number };
}) {
  return (
    <StatusCardsRow
      cards={page.statusCards}
      cases={page.statusCases}
      open={openCard}
      onToggle={setOpenCard}
      dateHeading={dateHeading}
      showExactStatus={showExactStatus}
      shareOf={shareOf}
    />
  );
}

// ---------------------------------------------------------------------------
// The facts block (#1267): can they receive Referrals, and what do we owe?
// ---------------------------------------------------------------------------

/** The documents the block lists, in this order — applicableArtifactsFor's
 *  set (the one source of which artifacts a designation makes applicable),
 *  less the logo and the primary Contact, which has its own line. */
const DOC_ROWS: { key: IntakeArtifact; label: string; file: (f: ClientSuccessFirm) => { name: string; url: string } | null }[] = [
  { key: "mfaH", label: "MFA — Handling", file: (f) => f.documents.mfaHandling },
  { key: "mfaO", label: "MFA — Originating", file: (f) => f.documents.mfaOriginating },
  { key: "w9", label: "W-9", file: (f) => f.documents.w9 },
  { key: "malpractice", label: "Malpractice insurance", file: (f) => f.documents.malpracticeInsurance },
];

/** The headline slot's sentence. Until Firm Success's foundation document
 *  (owed to chrisfnrose/LegalFlare-Firm-Success#136) rules which fact leads a
 *  Firm's page, the slot carries the Firm's routing status, as #1267 says. */
function headlineFor(firm: ClientSuccessFirm): string {
  if (firm.routingStatus === "Paused") return "Routing is paused.";
  if (firm.routingStatus === "Active") return "Routing is active.";
  return "Routing status is not recorded.";
}

/** Above the cards: a headline slot, then the Firm's routing status, the
 *  documents its designation makes applicable and its primary Contact — what
 *  decides whether it can receive Referrals and whether Firm Success owes it a
 *  document chase (#1267, from Review chrisfnrose/LegalFlare-Firm-Success#136).
 *  It reads the Firms tab's own roster (GET /api/firm-success/firms), matched
 *  by the Firm's record id, so the two Firm Success surfaces cannot disagree. */
function FirmFacts({ firmId }: { firmId: string }) {
  const firms = useClientSuccessFirms();
  if (firms.isLoading) return null;
  const firm = firms.data?.firms.find((f) => f.id === firmId);
  if (firm === undefined) {
    return (
      <p className="soft" data-testid="fs-facts-missing">
        {firms.isError ? "Could not load this firm's record." : "This firm's record is not in the Firm list."}
      </p>
    );
  }
  const applicable = new Set(applicableArtifactsFor(firm.designation));
  const exempt = new Set(firm.exemptArtifacts);
  const contact = firm.primaryContact;
  return (
    <>
      <p className="fs-headline" data-testid="fs-headline">
        {headlineFor(firm)}
      </p>
      <QCard title="Can they receive Referrals?" accent="var(--q-pipe)">
      <ul className="fs-facts" data-testid="fs-facts">
        <li>
          <span className="k">Routing</span>
          <span data-testid="fs-fact-routing">{firm.routingStatus ?? "not recorded"}</span>
        </li>
        <li>
          <span className="k">Primary Contact</span>
          <span data-testid="fs-fact-contact">
            {contact === null
              ? exempt.has("primaryContact")
                ? "Not required"
                : "No primary Contact on file"
              : [contact.name, contact.email, contact.phone].filter((x) => x !== null && x !== "").join(" · ")}
          </span>
        </li>
        {DOC_ROWS.filter((d) => applicable.has(d.key)).map((d) => {
          const file = d.file(firm);
          return (
            <li key={d.key}>
              <span className="k">{d.label}</span>
              <span data-testid={`fs-fact-doc-${d.key}`}>
                {file !== null ? (
                  <a href={file.url} target="_blank" rel="noreferrer">
                    on file
                  </a>
                ) : exempt.has(d.key) ? (
                  "Not required"
                ) : (
                  "missing"
                )}
              </span>
            </li>
          );
        })}
      </ul>
      </QCard>
    </>
  );
}
